import moment from 'moment'
import pdfIcon from '@/util/pdf-icon-64'
import router from '@/router'
import { months } from '@/util/SelectLists'

const appStatus = [
  'Accepted',
  'Confirmed',
  'Rejected',
  'Declined'
]

const initState = () => ({
  isLoading: false,
  isLoaded: false,
  isSaving: false,
  isNew: false,
  step: 0,
  stepEntered: 0,
  completed: false,
  teamLeader: true,
  financialAid: true,
  signedUrlPost: '',
  updatedAt: '',
  data: {
    step: 0,
    proofOfEnrollment: {
      dataURL: '',
      name: '',
      size: 0,
      type: ''
    },
    schoolName: '',
    schoolCity: '',
    schoolState: '',
    schoolCountry: '',
    expectedGraduationMonth: '',
    expectedGraduationYear: '',
    haveNotAttended: false,
    haveAttended: false,
    haveVolunteered: false,
    yearsVolunteered: [],
    haveAttendedAsia: false,
    haveVolunteeredAsia: false,
    yearsVolunteeredAsia: [],
    firstReferenceType: '',
    firstReferenceFirstName: '',
    firstReferenceLastName: '',
    firstReferenceEmail: '',
    secondReferenceType: '',
    secondReferenceFirstName: '',
    secondReferenceLastName: '',
    secondReferenceEmail: '',
    essay1: '',
    essay2: '',
    essay3: '',
    teamLeaderEssay1: '',
    teamLeaderEssay2: '',
    otherComments: '',
    financialAidSummary: '',
    financialAidExpenses: '',
    financialSituation: [],
    estimatedTravelCost: '',
    primaryTransport: '',
    airportCode: '',
    otherTransport: '',
    fundedTravel: false,
    acceptedTerms: false,
    acceptedMediaRelease: false,
    acceptedPrivacy: false
  },
  statusId: null,
  TLAppDeadline: '2026-01-13T22:00Z'
})

const fieldNames = {
  proofOfEnrollment: 'Proof Of Enrollment',
  schoolName: 'School Name',
  schoolCity: 'School City',
  schoolState: 'School State',
  schoolCountry: 'School Country',
  expectedGraduationMonth: 'Expected Month of Graduation',
  expectedGraduationYear: 'Expected Year of Graduation',
  firstReferenceType: 'First Reference Type',
  firstReferenceFirstName: 'First Reference First Name',
  firstReferenceLastName: 'First Reference Last Name',
  firstReferenceEmail: 'First Reference Email',
  secondReferenceType: 'Second Reference Type',
  secondReferenceFirstName: 'Second Reference First Name',
  secondReferenceLastName: 'Second Reference Last Name',
  secondReferenceEmail: 'Second Reference Email',
  essay1: 'First Essay',
  essay2: 'Second Essay',
  essay3: 'Third Essay',
  financialAidSummary: 'Financial Aid Summary',
  financialAidExpenses: 'List of Travel Expenses',
  estimatedTravelCost: 'Estimated Travel Cost',
  primaryTransport: 'Primary Mode of Transportation',
  airportCode: 'Airport Code',
  otherTransport: 'Transportation Explanation',
  teamLeaderEssay1: 'First Team Leader Essay',
  teamLeaderEssay2: 'Second Team Leader Essay',
  otherComments: 'Additional Comments',
  acceptedTerms: 'Acceptance of Terms',
  acceptedMediaRelease: 'Acceptance of Media Release Agreement',
  acceptedPrivacy: 'Acceptance of ACM Privacy Policy',
  yearsVolunteered: 'Years that you were an SV for SIGGRAPH North America',
  yearsVolunteeredAsia: 'Years that you were an SV for SIGGRAPH Asia'
}

const application = {
  namespaced: true,
  state: initState(),
  mutations: {
    loading (state) {
      state.isLoading = true
    },
    appLoaded (state, { data, step, updatedAt, completed, teamLeader, financialAid, statusId, status }) {
      state.isLoading = false
      state.isLoaded = true
      state.step = step
      state.updatedAt = updatedAt
      state.completed = completed
      state.teamLeader = teamLeader
      state.financialAid = financialAid
      state.statusId = statusId
      state.status = status
      state.data = Object.assign(initState().data, data)
    },
    saving (state) {
      state.isSaving = true
    },
    appSaved (state) {
      state.isSaving = false
      state.updatedAt = moment().utc()
      state.isNew = false
    },
    appLoadedInit (state) {
      state.isLoading = false
      state.isLoaded = true
      state.isNew = true
    },
    loadProofOfEnrollment (state, url) {
      state.data.proofOfEnrollment.dataURL = url
    },
    signedUrlPost (state, url) {
      state.signedUrlPost = url
    },
    fileUploaded (state, file) {
      console.log(file)
      state.data.proofOfEnrollment = file
    },
    appClearData (state) {
      const init = initState()
      Object.keys(init).forEach(key => { state[key] = init[key] })
    },
    updateField (state, { name, value }) {
      state.data[name] = value
    },
    removeProofOfEnrollment (state) {
      const init = initState().data.proofOfEnrollment
      Object.keys(init).forEach(key => { state.data.proofOfEnrollment[key] = init[key] })
    },
    updateFinancialAid (state, value) {
      state.financialAid = value
    },
    updateTeamLeader (state, value) {
      state.teamLeader = value
    },
    stepEntered (state, step) {
      state.stepEntered = step
    },
    updateStep (state, step) {
      state.step = step
      state.data.step = step
    },
    complete (state) {
      state.data.acceptedTerms = true
      state.data.acceptedMediaRelease = true
      state.completed = true
    },
    disableTeamLeader (state) {
      state.teamLeader = false
    }
  },
  getters: {
    getProgress: (state) => {
      if (state.completed) return 100
      let denominator = 0
      let init = initState()
      let invalidFields = Object.keys(init.data).filter(key => {
        switch (key) {
          case 'step':
          case 'haveNotAttended':
          case 'haveAttended':
          case 'haveVolunteered':
          case 'haveAttendedAsia':
          case 'haveVolunteeredAsia':
          case 'otherComments':
          case 'fundedTravel':
          case 'financialSituation':
            return false
          case 'proofOfEnrollment':
            if (init.data[key].name === state.data[key].name) {
              denominator++
              return true
            }
            return false
          case 'financialAidSummary':
          case 'financialAidExpenses':
          case 'estimatedTravelCost':
          case 'primaryTransport':
            if (state.financialAid && init.data[key] === state.data[key]) {
              denominator++
              return true
            }
            return false
          case 'airportCode':
            if (state.financialAid && state.data['primaryTransport'] === 'plane' && init.data[key] === state.data[key]) {
              denominator++
              return true
            }
            return false
          case 'otherTransport':
            if (state.financialAid && state.data['primaryTransport'] === 'other' && init.data[key] === state.data[key]) {
              denominator++
              return true
            }
            return false
          case 'teamLeaderEssay1':
          case 'teamLeaderEssay2':
            if (state.teamLeader && init.data[key] === state.data[key]) {
              denominator++
              return true
            }
            return false
          case 'yearsVolunteered':
            if (state.data['haveVolunteered'] && state.data[key].length === 0) {
              denominator++
              return true
            }
            return false
          case 'yearsVolunteeredAsia':
            if (state.data['haveVolunteeredAsia'] && state.data[key].length === 0) {
              denominator++
              return true
            }
            return false
          default:
            denominator++
            return init.data[key] === state.data[key]
        }
      })
      if (!state.data['haveNotAttended'] && !state.data['haveAttended'] && !state.data['haveVolunteered'] && !state.data['haveAttendedAsia'] && !state.data['haveVolunteeredAsia']) {
        invalidFields.push('SIGGRAPH Experience')
        denominator++
      }
      return Math.round((denominator - invalidFields.length) * 100 / denominator)
    },
    isNewApp: (state) => {
      return state.isLoaded === true && state.isNew === true
    },
    isNotNewApp: (state) => {
      return state.isLoaded === true && state.isNew === false
    },
    getDataURL: (state) => {
      if (state.data.proofOfEnrollment.dataURL !== '') {
        return state.data.proofOfEnrollment.dataURL
      }
      return pdfIcon
    },
    proofOfEnrollmentExists: (state) => {
      return state.data.proofOfEnrollment.name !== ''
    },
    proofOfEnrollmentFileSize: (state) => {
      return Math.round(state.data.proofOfEnrollment.size / 1000000 * 100) / 100
    },
    getExpectedGraduationMonth: (state) => {
      return months.find(row => {
        return row.value === state.data.expectedGraduationMonth
      })
    },
    getExpectedGraduationYear: (state) => {
      return state.data.expectedGraduationYear
    },
    getYearsVolunteered: (state) => {
      return state.data.yearsVolunteered
    },
    getYearsVolunteeredAsia: (state) => {
      return state.data.yearsVolunteeredAsia
    },
    getFinancialSituation: (state) => {
      return state.data.financialSituation
    },
    financialAidAndFlying: (state) => {
      return state.financialAid && state.data.primaryTransport === 'plane'
    },
    financialAidAndOtherTransport: (state) => {
      return state.financialAid && state.data.primaryTransport === 'other'
    },
    getVolunteerTypePrefFirst: (state) => {
      return state.data.firstChoice
    },
    getVolunteerTypePrefSecond: (state) => {
      return state.data.secondChoice
    },
    getTimeZone: (state) => {
      return state.data.timeZone
    },
    invalidFields: (state) => {
      let init = initState()
      let invalidFields = Object.keys(init.data).filter(key => {
        switch (key) {
          case 'haveNotAttended':
          case 'haveAttended':
          case 'haveVolunteered':
          case 'haveAttendedAsia':
          case 'haveVolunteeredAsia':
          case 'otherComments':
          case 'fundedTravel':
          case 'financialSituation':
            return false
          case 'proofOfEnrollment':
            return init.data[key].name === state.data[key].name
          case 'financialAidSummary':
          case 'financialAidExpenses':
          case 'estimatedTravelCost':
          case 'primaryTransport':
            return state.financialAid && init.data[key] === state.data[key]
          case 'airportCode':
            return state.financialAid && state.data['primaryTransport'] === 'plane' && init.data[key] === state.data[key]
          case 'otherTransport':
            return state.financialAid && state.data['primaryTransport'] === 'other' && init.data[key] === state.data[key]
          case 'teamLeaderEssay1':
          case 'teamLeaderEssay2':
            return state.teamLeader && init.data[key] === state.data[key]
          case 'yearsVolunteered':
            return state.data['haveVolunteered'] && state.data[key].length === 0
          case 'yearsVolunteeredAsia':
            return state.data['haveVolunteeredAsia'] && state.data[key].length === 0
          default:
            return init.data[key] === state.data[key]
        }
      })
      let fields = []
      invalidFields.forEach(key => {
        fields.push(fieldNames[key])
      })
      if (!state.data['haveNotAttended'] && !state.data['haveAttended'] && !state.data['haveVolunteered'] && !state.data['haveAttendedAsia'] && !state.data['haveVolunteeredAsia']) {
        fields.push('SIGGRAPH Experience')
      }
      return fields
    },
    isValid: (state) => {
      let init = initState()
      let invalidFields = Object.keys(init.data).filter(key => {
        switch (key) {
          case 'haveNotAttended':
          case 'haveAttended':
          case 'haveVolunteered':
          case 'haveAttendedAsia':
          case 'haveVolunteeredAsia':
          case 'otherComments':
          case 'fundedTravel':
          case 'financialSituation':
            return false
          case 'proofOfEnrollment':
            return init.data[key].name === state.data[key].name
          case 'financialAidSummary':
          case 'financialAidExpenses':
          case 'estimatedTravelCost':
          case 'primaryTransport':
            return state.financialAid && init.data[key] === state.data[key]
          case 'airportCode':
            return state.financialAid && state.data['primaryTransport'] === 'plane' && init.data[key] === state.data[key]
          case 'otherTransport':
            return state.financialAid && state.data['primaryTransport'] === 'other' && init.data[key] === state.data[key]
          case 'teamLeaderEssay1':
          case 'teamLeaderEssay2':
            return state.teamLeader && init.data[key] === state.data[key]
          case 'yearsVolunteered':
            return state.data['haveVolunteered'] && state.data[key].length === 0
          case 'yearsVolunteeredAsia':
            return state.data['haveVolunteeredAsia'] && state.data[key].length === 0
          default:
            return init.data[key] === state.data[key]
        }
      })
      if (!state.data['haveNotAttended'] && !state.data['haveAttended'] && !state.data['haveVolunteered'] && !state.data['haveAttendedAsia'] && !state.data['haveVolunteeredAsia']) {
        invalidFields.push('SIGGRAPH Experience')
      }
      return invalidFields.length === 0
    },
    isOverWordCount: (state) => {
      let init = initState()
      let invalidFields = Object.keys(init.data).filter(key => {
        switch (key) {
          case 'financialAidSummary':
            return state.financialAid && state.data[key].trim().split(/\s+/).length > 100
          case 'teamLeaderEssay1':
          case 'teamLeaderEssay2':
            return state.teamLeader && state.data[key].trim().split(/\s+/).length > 300
          case 'essay1':
          case 'essay2':
          case 'essay3':
            return state.data[key].trim().split(/\s+/).length > 300
          case 'otherComments':
            return state.data[key].trim().split(/\s+/).length > 100
          default:
            return false
        }
      })
      return invalidFields.length !== 0
    },
    overWordCount: (state) => {
      let init = initState()
      let invalidFields = Object.keys(init.data).filter(key => {
        switch (key) {
          case 'otherComments':
            return state.data[key].trim().split(/\s+/).length > 100
          case 'financialAidSummary':
            return state.financialAid && state.data[key].trim().split(/\s+/).length > 100
          case 'teamLeaderEssay1':
          case 'teamLeaderEssay2':
            return state.teamLeader && state.data[key].trim().split(/\s+/).length > 300
          case 'essay1':
          case 'essay2':
          case 'essay3':
            return state.data[key].trim().split(/\s+/).length > 300
          default:
            return false
        }
      })
      let fields = []
      invalidFields.forEach(key => {
        fields.push(fieldNames[key])
      })
      return fields
    },
    pastTLAppDeadline: (state) => {
      return moment().utc().isAfter(state.TLAppDeadline)
    },
    isPending: (state) => {
      return state.statusId === null || state.statusId === 0
    },
    isOffered: (state) => {
      return state.statusId === 1
    },
    isAccepted: (state) => {
      return state.statusId === 2
    },
    isDeclined: (state) => {
      return state.statusId === 4
    },
    isRejected: (state) => {
      return state.statusId === 3
    },
    isInvalidTLApp: (state) => {
      return state.teamLeader && !(state.data.haveVolunteered || state.data.haveVolunteeredAsia)
    }
  },
  actions: {
    load ({ commit, rootState }) {
      return new Promise((resolve, reject) => {
        commit('loading')
        fetch(process.env.BASE_URL + 'application/' + rootState.user.profile.id,
          {
            headers: {
              'Authorization': rootState.user.session.accessToken
            }
          })
          .then(response => {
            return response.json().then(payload => {
              if (response.status === 200 && payload.success) {
                let appData = {
                  data: payload.data.data,
                  step: payload.data.data.step,
                  updatedAt: payload.data.updated_at,
                  completed: payload.data.completed,
                  teamLeader: payload.data.team_leader,
                  financialAid: payload.data.financial_aid,
                  statusId: payload.data.sv.application_status_id,
                  status: payload.data.sv.application_status_id === null ? 'Pending' : appStatus[payload.data.sv.application_status_id - 1]
                }
                commit('appLoaded', appData)
                // resolve()
                if (appData.data.proofOfEnrollment.type === 'application/pdf') {
                  fetch(process.env.BASE_URL + 'files/' + rootState.user.profile.svid,
                    {
                      headers: {
                        'Authorization': rootState.user.session.accessToken
                      }
                    })
                    .then(response => {
                      response.json().then(payload => {
                        if (response.status === 200 && payload.success) {
                          commit('loadProofOfEnrollment', payload.data)
                          resolve()
                        } else {
                          commit('error', { message: 'Unable to load application' }, { root: true })
                          resolve()
                        }
                      })
                    }).catch(err => {
                      commit('error', { message: err.message }, { root: true })
                      reject(err)
                    })
                }
                resolve()
              } else if (response.status === 404 && !payload.success) {
                commit('appLoadedInit')
                resolve()
              } else {
                commit('error', { message: 'Unable to load application' }, { root: true })
                resolve()
              }
            })
          }).catch(err => {
            commit('error', { message: err.message }, { root: true })
            reject(err)
          })
      })
    },
    save ({ commit, state, rootState }) {
      return new Promise((resolve, reject) => {
        commit('saving')
        let appData = {
          sv_id: rootState.user.profile.id,
          data: state.data,
          completed: state.completed,
          team_leader: state.teamLeader,
          financial_aid: state.financialAid
        }
        fetch(process.env.BASE_URL + 'application/save', {
          method: 'POST',
          headers: {
            'Authorization': rootState.user.session.accessToken
          },
          body: JSON.stringify(appData)
        }).then((response) => {
          commit('appSaved')
          resolve()
        }).catch((err) => {
          commit('error', { message: err.message }, { root: true })
          reject(err)
        })
      })
    },
    getSignedUrl ({ commit, rootState }, { fileType }) {
      return new Promise((resolve, reject) => {
        fetch(process.env.BASE_URL + 'files/upload', {
          method: 'POST',
          headers: {
            'Authorization': rootState.user.session.accessToken
          },
          body: JSON.stringify({
            user_id: rootState.user.profile.svid,
            file_type: fileType
          })
        }).then(response => {
          response.json().then(payload => {
            commit('signedUrlPost', payload.data.url)
            resolve(payload.data.url)
          })
        }).catch(err => {
          commit('error', { message: err.message }, { root: true })
        })
      })
    },
    finish ({ commit, dispatch }) {
      commit('complete')
      dispatch('save').then(() => {
        router.push('/')
      })
    }
  }
}

export default application



// WEBPACK FOOTER //
// ./src/store/application.js